import mitt, { Emitter } from "mitt";
import type { ConversationMessagePromptInfo } from "@shaarp/common";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A question sent to the chat from elsewhere in the app, with the tactic it
 * was sent from when there is one — so the answer is traced to that tactic
 * and its bubble shows the tactic's pill, as from the composer (#681).
 */
type ChatActionMessage = {
  text: string;
  prompt?: ConversationMessagePromptInfo;
};

/**
 * Chat action event types
 */
type ChatActionEvents = {
  openChat: void;
  sendMessage: ChatActionMessage;
};

/**
 * Singleton event emitter for chat actions
 * Using mitt for lightweight, type-safe events
 */
const chatActionEmitter: Emitter<ChatActionEvents> = mitt<ChatActionEvents>();

/**
 * Buffer for messages emitted before any listener is registered.
 * This solves the race condition where sendMessage fires before the
 * chat panel has mounted its listener (e.g. first click opens the panel,
 * but the message event is lost because ChatTextInput isn't rendered yet).
 */
let pendingMessage: ChatActionMessage | null = null;

/**
 * When the buffered message was sent. A listener that mounts later than this
 * takes nothing: the message belonged to a chat that was meant to open at
 * once, and sending it on some later visit would ask a question the user has
 * long moved on from. The chat is in the bundle already (no route is lazy):
 * a navigation and a render is all it waits for.
 */
let pendingMessageSentAt = 0;
export const PENDING_MESSAGE_TTL_MS = 10_000;

/**
 * Hook to emit chat actions (send message, open chat)
 * Used by CTA buttons to trigger chat actions
 */
export function useChatActionEmitter() {
  const [isPending, setIsPending] = useState(false);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Send a message to the chat
   * This will buffer the message and emit the 'openChat' event
   * @param message - The message to send
   * @param prompt - The tactic the message was sent from, if any
   */
  const sendChatMessage = useCallback(
    (message: string, prompt?: ConversationMessagePromptInfo) => {
      if (isPending) return;

      // Set pending state to prevent rapid clicks
      setIsPending(true);

      // Clear any existing timeout
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }

      // Buffer the message so the listener can pick it up after mounting
      const chatMessage: ChatActionMessage = { text: message, prompt };
      pendingMessage = chatMessage;
      pendingMessageSentAt = Date.now();

      // Open the chat panel, then emit the message for already-mounted listeners
      chatActionEmitter.emit("openChat");
      chatActionEmitter.emit("sendMessage", chatMessage);

      // Reset pending state after 3 seconds (enough time for stream to start)
      pendingTimeoutRef.current = setTimeout(() => {
        setIsPending(false);
      }, 3000);
    },
    [isPending]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Clear the pending state manually (e.g., when stream starts)
   */
  const clearPending = useCallback(() => {
    setIsPending(false);
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, []);

  return {
    sendChatMessage,
    isPending,
    clearPending,
  };
}

/**
 * Hook to listen for chat open events
 * Used by the company page to open the chat panel
 */
export function useChatOpenListener(onOpen: () => void) {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    const handler = () => {
      onOpenRef.current();
    };

    chatActionEmitter.on("openChat", handler);
    return () => {
      chatActionEmitter.off("openChat", handler);
    };
  }, []);
}

/**
 * Hook to consume a buffered chat message after the listener component mounts.
 * Used by ChatTextInput to receive and send messages from CTA buttons.
 */
export function useChatMessageListener(
  onMessage: (message: string, prompt?: ConversationMessagePromptInfo) => void,
  enabled = true,
  /** A buffered question too old to send, dropped: say so rather than lose it silently. */
  onExpired?: (message: string) => void
) {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!enabled) return;

    // Consume any message that was buffered before this listener mounted
    if (pendingMessage !== null) {
      const msg = pendingMessage;
      const isFresh = Date.now() - pendingMessageSentAt < PENDING_MESSAGE_TTL_MS;
      pendingMessage = null;
      queueMicrotask(() => {
        if (isFresh) onMessageRef.current(msg.text, msg.prompt);
        else onExpiredRef.current?.(msg.text);
      });
    }

    // Listen for future messages when already mounted
    const handler = (message: ChatActionMessage) => {
      pendingMessage = null;
      onMessageRef.current(message.text, message.prompt);
    };
    chatActionEmitter.on("sendMessage", handler);
    return () => {
      chatActionEmitter.off("sendMessage", handler);
    };
  }, [enabled]);
}
